import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { EventCalendar, type EventColor } from "~/components/event-calendar";
import { useCalendarContext } from "~/components/event-calendar/calendar-context";
import { AppContainer } from "~/components/layouts/app/app-container";
import { Card } from "~/components/ui/card";
import { useTRPC } from "~/trpc/client";

export const Route = createFileRoute("/_app/manager/planning/")({
	component: Page,
	async loader({ context }) {
		context.queryClient.prefetchQuery(
			context.trpc.episode.listAll.queryOptions(),
		);
	},
});

function Page() {
	const trpc = useTRPC();
	const queryClient = useQueryClient();

	const { data: episodes, isFetching } = useQuery(
		trpc.episode.listAll.queryOptions(),
	);

	const events = useMemo(
		() =>
			(episodes?.filter((episode) => episode.publishDate) ?? []).map(
				(episode) => {
					return {
						id: episode.uid,
						title: episode.title,
						description: episode.description,
						start: new Date(episode.publishDate!),
						end: new Date(episode.publishDate!),
						color: episode.themeColor as EventColor,
						label: episode.title,
						location: episode.channelsTitle,
						episodeNum: episode.episodeNum,
						seasonNum: episode.seasonNum,
						typeId: episode.typeID,
					};
				},
			),
		[episodes],
	);
	const { isColorVisible } = useCalendarContext();

	// Filter events based on visible colors
	const visibleEvents = useMemo(() => {
		return events.filter((event) => isColorVisible(event.color));
	}, [events, isColorVisible]);

	const handleEventDelete = (eventId: string) => {
		queryClient.setQueryData(
			trpc.episode.listAll.queryKey(),
			episodes?.filter((episode) => episode.uid !== eventId),
		);
	};

	return (
		<AppContainer className="my-12">
			<Card className="p-0">
				<EventCalendar
					events={visibleEvents}
					onEventDelete={handleEventDelete}
					initialView="month"
					loading={isFetching}
				/>
			</Card>
		</AppContainer>
	);
}
